/**
 * Compliance Gauge Component
 * Displays circular progress gauge for waste diversion rate
 */


import { formatPercentage, formatWeight } from '../../utils/formatting';
import { getDiversionRateColor, CALGREEN_DIVERSION_THRESHOLD } from '../../utils/calgreen';

interface ComplianceGaugeProps {
  diversionRate: number; // 0-100
  totalGenerated: number;
  totalDiverted: number;
  isCompliant: boolean;
}

export function ComplianceGauge({
  diversionRate,
  totalGenerated,
  totalDiverted,
  isCompliant,
}: ComplianceGaugeProps) {
  // Calculate circle dimensions
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (diversionRate / 100) * circumference;

  const textColor = getDiversionRateColor(diversionRate);
  const statusColor = isCompliant ? 'text-accent-600' : 'text-red-600';
  const statusBgColor = isCompliant ? 'bg-accent-100' : 'bg-red-100';

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-neutral-900 mb-6">
        CalGreen Compliance Status
      </h3>

      <div className="flex flex-col items-center">
        {/* Circular gauge */}
        <div className="relative w-48 h-48 mb-6">
          <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 200 200">
            {/* Background circle */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="currentColor"
              strokeWidth="16"
              fill="none"
              className="text-neutral-200"
            />
            {/* Progress circle */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="currentColor"
              strokeWidth="16"
              fill="none"
              className={isCompliant ? 'text-accent-500' : 'text-red-500'}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{
                transition: 'stroke-dashoffset 0.5s ease-in-out',
              }}
            />
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={`text-4xl font-bold ${textColor}`}>
              {formatPercentage(diversionRate, 1)}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Diverted</div>
          </div>
        </div>

        {/* Status badge */}
        <div
          className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusColor} ${statusBgColor} mb-4`}
        >
          {isCompliant ? (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Meets CalGreen 5.408 Requirements
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Does Not Meet Requirements
            </>
          )}
        </div>

        {/* Statistics */}
        <div className="w-full grid grid-cols-2 gap-4 text-center border-t border-neutral-200 pt-4">
          <div>
            <div className="text-sm text-neutral-600 mb-1">Total Generated</div>
            <div className="text-lg font-semibold text-neutral-900">
              {formatWeight(totalGenerated)}
            </div>
          </div>
          <div>
            <div className="text-sm text-neutral-600 mb-1">Total Diverted</div>
            <div className="text-lg font-semibold text-neutral-900">
              {formatWeight(totalDiverted)}
            </div>
          </div>
        </div>

        {/* Requirement info */}
        <div className="w-full mt-4 p-3 bg-neutral-50 rounded-md">
          <p className="text-xs text-neutral-600 text-center">
            CalGreen 5.408 requires a minimum {CALGREEN_DIVERSION_THRESHOLD}% diversion rate for
            construction and demolition waste.
          </p>
        </div>
      </div>
    </div>
  );
}